import { z } from 'zod';
import { EntityId } from './ids';

export const SyncOp = z.enum([
  'capture.upsert',
  'capture.delete',
  'session.upsert',
  'session.submit',
  'fieldValue.upsert',
  'media.upload',
  'signature.upload',
]);
export type SyncOp = z.infer<typeof SyncOp>;

export const SyncStatus = z.enum([
  'queued',
  'uploading',
  'succeeded',
  'conflict',
  'failed',
  'retrying',
]);
export type SyncStatus = z.infer<typeof SyncStatus>;

export const SyncTask = z.object({
  /**
   * Also the `Idempotency-Key` sent with the op (spec §4). Minted by
   * `enqueue()` with nanoid, so it is not a UUID and never was.
   */
  id: z.string().min(1),
  op: SyncOp,
  /** Entity this op acts on — capture id, session id, etc. */
  entityId: z.string(),
  /** Order within the session's causal chain (monotonic increment). */
  seq: z.number().int().nonnegative(),
  /** JSON payload sent to the server. */
  payload: z.record(z.unknown()),
  /** For media ops, an OPFS path to the blob to upload. */
  blobPath: z.string().nullable().default(null),
  /**
   * For media ops, the mime the recorder produced.
   *
   * Carried rather than derived: neither platform can infer it from the
   * stored file. Null on rows queued before this existed — the media store
   * falls back to its extension table.
   */
  mime: z.string().nullable().default(null),
  /**
   * Task this one must wait for. Null means "nothing".
   *
   * `seq` orders the queue but cannot express a dependency once more than one
   * task is in flight, and the ordering it implied was wrong anyway: a
   * photo's `media.upload` sorted ahead of the `capture.upsert` that tells
   * the server the capture exists.
   */
  dependsOn: z.string().nullable().default(null),
  /** Total bytes to upload (for progress UI). */
  byteSize: z.number().int().nonnegative().default(0),
  status: SyncStatus.default('queued'),
  attempts: z.number().int().nonnegative().default(0),
  lastError: z.string().nullable().default(null),
  /**
   * Server row version this op was built against, sent as `If-Match` for
   * optimistic concurrency. Integer, matching the server's `version` column —
   * a null here means "no check", which is last-write-wins.
   */
  baseVersion: z.number().int().nullable().default(null),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type SyncTask = z.infer<typeof SyncTask>;

export const ConflictResolution = z.enum(['keep-local', 'keep-remote', 'merge', 'defer']);
export type ConflictResolution = z.infer<typeof ConflictResolution>;

export const SyncConflict = z.object({
  // `nanoid()`, not a uuid — see EntityId.
  taskId: EntityId,
  field: z.string(),
  localValue: z.unknown(),
  remoteValue: z.unknown(),
  remoteEditor: z.object({
    id: z.string(),
    name: z.string(),
    at: z.string().datetime(),
  }),
});
export type SyncConflict = z.infer<typeof SyncConflict>;
